import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  CheckCircle2, Circle, Clock, Camera, Plus, Wrench, Package,
  ImagePlus, AlertCircle,
} from 'lucide-react';
import { AuthContext } from '../App';
import { toast } from 'sonner';

const PART_STATUS_LABEL = {
  pending: { label: 'Pendiente', color: 'bg-slate-100 text-slate-700' },
  ordered: { label: 'Pedido', color: 'bg-amber-100 text-amber-700' },
  received: { label: 'Recibido', color: 'bg-blue-100 text-blue-700' },
  installed: { label: 'Instalado', color: 'bg-emerald-100 text-emerald-700' },
};

const STAGE_STATUS_ICON = {
  completed: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
  in_progress: <Clock className="w-5 h-5 text-amber-500 animate-pulse" />,
  pending: <Circle className="w-5 h-5 text-slate-300" />,
};

const RepairTimeline = ({ appointmentId, readOnly = false }) => {
  const { API, sessionToken } = useContext(AuthContext);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const [newPart, setNewPart] = useState({ name: '', status: 'pending', notes: '' });
  const [advanceNotes, setAdvanceNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${sessionToken}` };

  useEffect(() => {
    if (appointmentId) {
      fetchProgress();
      fetchPhotos();
    }
  }, [appointmentId]);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/appointments/${appointmentId}/progress`, { headers });
      setProgress(res.data);
    } catch (e) {
      console.error('Error fetching repair progress:', e);
    } finally {
      setLoading(false);
    }
  };

  const advanceStage = async (newStage) => {
    if (readOnly) return;
    try {
      setAdvancing(true);
      const res = await axios.post(
        `${API}/appointments/${appointmentId}/progress/advance`,
        { stage: newStage, notes: advanceNotes || undefined },
        { headers }
      );
      setProgress(res.data);
      setAdvanceNotes('');
      toast.success(`Etapa actualizada: ${res.data.stages.find(s => s.stage === newStage)?.label}`);
    } catch (e) {
      toast.error('No se pudo actualizar la etapa');
    } finally {
      setAdvancing(false);
    }
  };

  const addPart = async () => {
    if (!newPart.name) {
      toast.error('Nombre del repuesto requerido');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/appointments/${appointmentId}/parts`,
        newPart,
        { headers }
      );
      setProgress(res.data);
      setNewPart({ name: '', status: 'pending', notes: '' });
      toast.success('Repuesto añadido');
    } catch {
      toast.error('Error al añadir repuesto');
    }
  };

  const updatePartStatus = async (partId, newStatus) => {
    try {
      const res = await axios.put(
        `${API}/appointments/${appointmentId}/parts/${partId}`,
        { status: newStatus },
        { headers }
      );
      setProgress(res.data);
      toast.success('Estado de repuesto actualizado');
    } catch {
      toast.error('Error al actualizar repuesto');
    }
  };

  const fetchPhotos = async () => {
    try {
      const res = await axios.get(`${API}/appointments/${appointmentId}/photos`, { headers });
      setPhotos(res.data);
    } catch (e) {
      console.error('Error fetching photos:', e);
    }
  };

  const handlePhotoUpload = (stageKey, type) => {
    setPendingUpload({ stage: stageKey, type });
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !pendingUpload) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('La foto supera el límite de 8MB');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('stage', pendingUpload.stage);
    formData.append('photo_type', pendingUpload.type);
    try {
      setUploading(true);
      await axios.post(`${API}/appointments/${appointmentId}/photos`, formData, { headers });
      toast.success('Foto subida correctamente');
      fetchPhotos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir la foto');
    } finally {
      setUploading(false);
      setPendingUpload(null);
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/photos/${photoId}`, { headers });
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      toast.success('Foto eliminada');
    } catch {
      toast.error('No se pudo eliminar la foto');
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500 py-4">Cargando progreso de reparación…</div>;
  }

  if (!progress) {
    return <div className="text-sm text-slate-500 py-4">No hay información de progreso disponible.</div>;
  }

  const stages = (progress.stages || []).sort((a, b) => a.order - b.order);
  const currentIdx = stages.findIndex(s => s.stage === progress.current_stage);

  return (
    <div className="space-y-6" data-testid="repair-timeline">
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        ref={fileInputRef}
        className="hidden"
        onChange={onFileSelected}
        data-testid="photo-file-input"
      />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-purple-600" />
            Progreso de reparación
          </h3>
          <p className="text-sm text-slate-500">
            Etapa actual: <span className="font-medium text-slate-700">
              {stages[currentIdx]?.label || progress.current_stage}
            </span>
          </p>
        </div>
        <Badge variant="outline" className="text-xs">
          {currentIdx + 1} / {stages.length}
        </Badge>
      </div>

      {/* Timeline */}
      <Card className="p-6">
        <div className="space-y-4">
          {stages.map((stage, idx) => {
            const isActive = stage.status === 'in_progress';
            const isDone = stage.status === 'completed';
            const isPending = stage.status === 'pending';
            const stagePhotos = photos.filter((p) => p.stage === stage.stage);

            return (
              <div key={stage.stage} className="flex gap-4" data-testid={`stage-${stage.stage}`}>
                {/* Icon + line */}
                <div className="flex flex-col items-center">
                  <div className={`rounded-full p-1 ${isActive ? 'bg-amber-100' : isDone ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                    {STAGE_STATUS_ICON[stage.status]}
                  </div>
                  {idx < stages.length - 1 && (
                    <div className={`w-0.5 flex-1 my-1 ${isDone ? 'bg-emerald-300' : 'bg-slate-200'}`} style={{ minHeight: 32 }} />
                  )}
                </div>

                {/* Content */}
                <div className={`flex-1 pb-4 ${isPending ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className={`font-medium ${isActive ? 'text-amber-700' : isDone ? 'text-slate-900' : 'text-slate-600'}`}>
                        {stage.label}
                      </p>
                      {stage.updated_at && (
                        <p className="text-xs text-slate-400">
                          Actualizado: {new Date(stage.updated_at).toLocaleString('es-CR')}
                        </p>
                      )}
                      {stage.notes && (
                        <p className="text-sm text-slate-600 mt-1 bg-slate-50 px-2 py-1 rounded inline-block">
                          {stage.notes}
                        </p>
                      )}
                    </div>

                    {!readOnly && isActive && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const next = stages[idx + 1];
                          if (next) advanceStage(next.stage);
                        }}
                        disabled={advancing || idx === stages.length - 1}
                        data-testid={`advance-from-${stage.stage}`}
                      >
                        Avanzar →
                      </Button>
                    )}
                  </div>

                  {/* Stage photos gallery */}
                  {stagePhotos.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid={`stage-photos-${stage.stage}`}>
                      {stagePhotos.map((p) => (
                        <div key={p.id} className="relative group">
                          <a href={`${API}/photos/${p.id}`} target="_blank" rel="noreferrer">
                            <img
                              src={`${API}/photos/${p.id}`}
                              alt={`Foto ${p.photo_type}`}
                              className="w-20 h-20 object-cover rounded-lg border border-slate-200"
                              loading="lazy"
                            />
                          </a>
                          <span className="absolute bottom-1 left-1 text-[9px] font-semibold uppercase bg-slate-900/70 text-white px-1.5 py-0.5 rounded">
                            {p.photo_type}
                          </span>
                          {!readOnly && (
                            <button
                              onClick={() => deletePhoto(p.id)}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full text-[10px] leading-none items-center justify-center hidden group-hover:flex"
                              data-testid={`delete-photo-${p.id}`}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Photo upload for active stage */}
                  {!readOnly && isActive && (
                    <div className="mt-3 space-y-2">
                      <Textarea
                        placeholder="Notas para esta etapa (opcional)"
                        value={advanceNotes}
                        onChange={(e) => setAdvanceNotes(e.target.value)}
                        className="text-sm"
                        rows={2}
                        data-testid="advance-notes-input"
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          disabled={uploading}
                          onClick={() => handlePhotoUpload(stage.stage, 'antes')}
                          data-testid="upload-photo-before"
                        >
                          <ImagePlus className="w-3 h-3 mr-1" /> {uploading && pendingUpload?.type === 'antes' ? 'Subiendo…' : 'Subir foto: Antes'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          disabled={uploading}
                          onClick={() => handlePhotoUpload(stage.stage, 'durante')}
                          data-testid="upload-photo-during"
                        >
                          <ImagePlus className="w-3 h-3 mr-1" /> {uploading && pendingUpload?.type === 'durante' ? 'Subiendo…' : 'Subir foto: Durante'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          disabled={uploading}
                          onClick={() => handlePhotoUpload(stage.stage, 'despues')}
                          data-testid="upload-photo-after"
                        >
                          <ImagePlus className="w-3 h-3 mr-1" /> {uploading && pendingUpload?.type === 'despues' ? 'Subiendo…' : 'Subir foto: Después'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Parts management */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Package className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-slate-900">Estado de repuestos</h3>
        </div>

        {progress.parts && progress.parts.length > 0 ? (
          <div className="space-y-2 mb-4">
            {progress.parts.map((p) => {
              const status = PART_STATUS_LABEL[p.status] || PART_STATUS_LABEL.pending;
              return (
                <div key={p.id} className="flex items-center justify-between gap-3 p-3 bg-slate-50 rounded" data-testid={`part-${p.id}`}>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{p.name}</p>
                    {p.notes && <p className="text-xs text-slate-500">{p.notes}</p>}
                  </div>
                  {readOnly ? (
                    <span className={`text-xs px-2 py-1 rounded ${status.color}`}>{status.label}</span>
                  ) : (
                    <Select value={p.status} onValueChange={(v) => updatePartStatus(p.id, v)}>
                      <SelectTrigger className="w-32 h-8 text-xs" data-testid={`part-status-${p.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pendiente</SelectItem>
                        <SelectItem value="ordered">Pedido</SelectItem>
                        <SelectItem value="received">Recibido</SelectItem>
                        <SelectItem value="installed">Instalado</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500 mb-4">No hay repuestos registrados todavía.</p>
        )}

        {!readOnly && (
          <div className="border-t pt-4 space-y-2">
            <p className="text-xs uppercase text-slate-500 font-medium">Añadir repuesto</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="Nombre del repuesto"
                value={newPart.name}
                onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                data-testid="part-name-input"
              />
              <Select value={newPart.status} onValueChange={(v) => setNewPart({ ...newPart, status: v })}>
                <SelectTrigger className="w-full sm:w-36" data-testid="part-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="ordered">Pedido</SelectItem>
                  <SelectItem value="received">Recibido</SelectItem>
                  <SelectItem value="installed">Instalado</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={addPart} data-testid="add-part-btn">
                <Plus className="w-4 h-4 mr-1" /> Añadir
              </Button>
            </div>
          </div>
        )}
      </Card>

      {readOnly && (
        <div className="text-xs text-slate-400 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Vista solo lectura — el taller actualiza el progreso.
        </div>
      )}
    </div>
  );
};

export default RepairTimeline;
